import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ExportCsvButton } from './ExportCsvButton';
import api from '../../lib/api';

const ROWS = [
  ['Full name', (p) => p.full_name],
  ['Preferred name', (p) => p.preferred_name],
  ['Pronouns', (p) => p.pronouns],
  ['Phone', (p) => p.phone],
  ['Email', (p) => p.email],
  ['Mailing address', (p) => p.mailing_address],
  ['Profession', (p) => p.profession],
  ['Birthday', (p) => p.birthday],
  ['Married', (p) => p.married],
  ['Children', (p) => p.children],
  ['Joined club', (p) => p.joined_date],
  ['Focus areas', (p) => (p.focus_areas || []).join(', ')],
  ['Newsletter help', (p) => p.newsletter_help],
  ['Book club', (p) => p.book_club],
  ['Committees', (p) => (p.committees || []).join(', ')],
  ['Skills & talents', (p) => p.skills],
  ['Ideas', (p) => p.ideas],
  ['T-shirt fit', (p) => p.shirt_fit],
  ['T-shirt size', (p) => (p.shirt_size === 'Other' ? p.shirt_size_other : p.shirt_size)],
  ['Photo OK (member-only)', (p) => p.photo_internal],
  ['Photo OK (public)', (p) => p.photo_public],
  ['Directory consent', (p) => p.directory_consent],
  ['Directory fields', (p) => (p.directory_fields || []).join(', ')],
];

export const ProfilesAdmin = () => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get('/admin/member-profiles').then((res) => setMembers(res.data)).catch(() => {});
  }, []);

  const shirtCounts = {};
  members.forEach((m) => {
    const p = m.profile || {};
    if (!p.shirt_size) return;
    const size = p.shirt_size === 'Other' ? (p.shirt_size_other || 'Other') : p.shirt_size;
    const key = `${p.shirt_fit || 'Any fit'} · ${size}`;
    shirtCounts[key] = (shirtCounts[key] || 0) + 1;
  });
  const shirtTotal = Object.values(shirtCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl text-[#3c4a2a]">Member Form Responses</h2>
          <p className="text-sm text-[#3c4a2a]/60 mt-1">
            Every member's information form — committees, t-shirt sizes, interests, and consents.
          </p>
        </div>
        <ExportCsvButton
          endpoint="/admin/member-profiles"
          filename="wclc-form-responses.csv"
          testId="export-profiles-btn"
          columns={[
            { label: 'Name', value: (r) => `${r.first_name} ${r.last_name}`.trim() },
            { label: 'Email', key: 'email' },
            { label: 'Form Completed', value: (r) => (r.completed ? 'Yes' : 'No') },
            ...ROWS.map(([label, fn]) => ({ label, value: (r) => fn(r.profile) || '' })),
          ]}
        />
      </div>

      <div className="bg-[#f1e3eb]/60 rounded-xl p-5" data-testid="shirt-summary">
        <h3 className="font-serif text-lg text-[#3c4a2a] mb-1">T-Shirt Order Summary</h3>
        {shirtTotal === 0 ? (
          <p className="text-sm text-[#3c4a2a]/50">No t-shirt preferences submitted yet.</p>
        ) : (
          <>
            <p className="text-sm text-[#3c4a2a]/60 mb-3">{shirtTotal} member{shirtTotal !== 1 ? 's' : ''} with a size on file</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(shirtCounts).sort().map(([key, count]) => (
                <Badge key={key} className="bg-white border border-[#bd8cae]/40 text-[#3c4a2a] px-3 py-1.5 text-sm" data-testid={`shirt-count-${key.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  {key}: <span className="font-bold ml-1">{count}</span>
                </Badge>
              ))}
            </div>
          </>
        )}
      </div>

      <Accordion type="single" collapsible className="w-full" data-testid="profiles-admin-list">
        {members.map((m) => (
          <AccordionItem key={m.id} value={m.id} className="border-b border-[#bd8cae]/30">
            <AccordionTrigger className="hover:no-underline py-4" data-testid={`profile-row-${m.member_id || m.id}`}>
              <span className="flex items-center gap-3 text-left">
                <span className="font-medium text-[#3c4a2a]">{m.first_name} {m.last_name}</span>
                <span className="text-sm text-[#3c4a2a]/50">{m.email}</span>
                <Badge className={m.completed ? 'bg-[#5d6b3c]/10 text-[#5d6b3c]' : 'bg-[#3c4a2a]/10 text-[#3c4a2a]/50'}>
                  {m.completed ? 'Form Completed' : 'Not Submitted'}
                </Badge>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {!m.completed ? (
                <p className="text-sm text-[#3c4a2a]/50 pb-2">This member hasn't filled out the information form yet.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 pb-3">
                  {ROWS.map(([label, fn]) => {
                    const val = fn(m.profile);
                    if (!val) return null;
                    return (
                      <div key={label} className="text-sm">
                        <span className="text-[#3c4a2a]/50">{label}: </span>
                        <span className="text-[#3c4a2a] whitespace-pre-wrap">{val}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
